import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useDataFetch from "../hooks/useDataFetch";
import { MobileResponsive } from "./MobileResponsive";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const ManageDrivers = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useDataFetch(
    "/admin/onboarding-new-drivers-list"
  );

  const [inputStates, setInputStates] = useState({
    searchDriver: { isFocused: false, isFilled: false, value: "" },
  });
  const [filteredDrivers, setFilteredDrivers] = useState([]);

  useEffect(() => {
    if (data?.data) {
      setFilteredDrivers(data.data);
    }
  }, [data]);

  const handleInputChange = (event, inputField) => {
    const value = event.target.value;

    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: {
        ...prevState[inputField],
        value: value,
        isFilled: value !== "",
      },
    }));

    if (inputField === "searchDriver") {
      if (value.trim() === "") {
        setFilteredDrivers(data?.data || []);
      } else {
        const filtered = (data?.data || []).filter((driver) =>
          driver?.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredDrivers(filtered);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleFocus = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: true },
    }));
  };

  const handleBlur = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: false },
    }));
  };

  const handleDriverClick = (driverId) => {
    navigate(`/manage-drivers/${driverId}`);
  };

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Review and Onboard New Drivers"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between">
            <Link to="/drivers" className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">
                New driver requests
              </span>
            </Link>
            <div className="flex items-center">
              <form
                action=""
                className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] w-[30vw]"
                onSubmit={handleSubmit}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search driver name"
                    className={`peer rounded-full py-2 text-[#F7631D] placeholder:text-transparent text-base font-medium w-full outline-none px-1 placeholder:select-none`}
                    value={inputStates.searchDriver.value}
                    onInput={(e) => handleInputChange(e, "searchDriver")}
                    onFocus={() => handleFocus("searchDriver")}
                    onBlur={() => handleBlur("searchDriver")}
                  />
                  <label
                    className={`absolute left-1 transform transition-all duration-200 ${inputStates.searchDriver.isFocused ||
                      inputStates.searchDriver.isFilled
                      ? "-top-0.5 -translate-y-1/2 left-2 text-xs px-1 bg-white rounded-full"
                      : "top-1/2 -translate-y-1/2 left-3 text-base"
                      } text-[#F7631D] pointer-events-none`}
                  >
                    Search driver name
                  </label>
                </div>
                <button type="submit">
                  <img src="/assets/search.svg" alt="Search" />
                </button>
              </form>
            </div>
            <div className="flex items-center text-[#413F3F]">
              <span className="font-semibold text-base">
                <span className="font-bold">{data?.totalCount || 0}</span> New
                {data?.totalCount !== 1 ? " Requests" : " Request"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-3 gap-y-5 w-[85%] py-8">
            {filteredDrivers && filteredDrivers.length > 0 ? (
              filteredDrivers.map((driver) => (
                  <div
                    key={driver?._id}
                    className="bg-[#FFFFFF] rounded-lg flex items-center justify-between border-[0.5px] border-[#00000014] shadow-custom4 px-5 py-2.5 cursor-pointer"
                    onClick={() => handleDriverClick(driver?._id)}
                  >
                    <div className="flex items-center gap-5">
                      {/* <img
                        src="/assets/user.png"
                        alt={driver?.name}
                        className="h-14"
                      /> */}
                      {driver?.driver_image ? (
                        driver?.driver_image?.includes("img.freepik.com") ? (
                          <Avatar
                            name={driver?.name}
                            size="52"
                            round={true}
                            maxInitials={2}
                            textSizeRatio={2.5}
                            style={{ letterSpacing: "1px" }}
                          />
                        ) : (
                          <img
                            src={driver?.driver_image}
                            alt=""
                            className="h-[52px] rounded-full"
                          />
                        )
                      ) : (
                        <Avatar
                          name={driver?.name}
                          size="52"
                          round={true}
                          maxInitials={2}
                          textSizeRatio={2.5}
                          style={{ letterSpacing: "1px" }}
                        />
                      )}
                      <p className="text-[17px] font-semibold tracking-wide capitalize">
                        {driver?.name}
                      </p>
                    </div>
                    <img
                      src="/assets/right-arrow.svg"
                      alt=""
                      className="h-[26px]"
                    />
                  </div>
                ))
            ) : (
              <div className="col-span-3 flex items-center justify-center py-10">
                <p className="text-gray-600 font-medium">
                  No new driver requests
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageDrivers;
